import { useUnsafeEffect } from "@wags/tanstack-start/react";
import { useState } from "react";
import { authClient } from "@/lib/core/auth-client";
import { PHONE_MEMBER_PLACEHOLDER_NAME } from "@/lib/core/auth-protocol/auth-phone.contract";
import { oauthErrorCallbackUrl } from "@/lib/core/auth-protocol/auth-redirect";
import {
    type GoogleSignInStartStatus,
    startGoogleSignInWith,
} from "@/lib/core/auth-protocol/google-sign-in-start";
import { useHydratedValue } from "@/lib/core/use-hydrated-value";

export function memberNameParts(name: string) {
    const trimmed = name.trim();
    const parts =
        trimmed === PHONE_MEMBER_PLACEHOLDER_NAME
            ? []
            : trimmed.split(/\s+/u).filter(Boolean);
    return {
        firstName: parts[0] ?? null,
        lastName: parts.length > 1 ? parts.slice(1).join(" ") : null,
    };
}

type SessionState = {
    data: { user: { id: string; isAnonymous?: boolean | null } } | null;
    error: Error | null;
    isPending: boolean;
};

export function sessionViewerId({ data, error, isPending }: SessionState) {
    if (isPending || (!data && error)) return undefined;
    return data && !data.user.isAnonymous ? data.user.id : null;
}

export function watchSessionViewer(
    onChange: (userId: string | null | undefined) => void,
) {
    return authClient.$store.atoms.session.subscribe((session: SessionState) =>
        onChange(sessionViewerId(session)),
    );
}

function useHydratedSession() {
    return useHydratedValue(authClient.useSession(), {
        data: null,
        isPending: true,
    });
}

export function useAuth() {
    const session = useHydratedSession();
    const identified = Boolean(session.data && !session.data.user.isAnonymous);
    return {
        authUserId: session.data?.user.id ?? null,
        isLoaded: !session.isPending,
        isSignedIn: identified,
        userId: identified ? (session.data?.user.id ?? null) : null,
    };
}

export function useUser() {
    const session = useHydratedSession();
    const user = session.data?.user;
    const identified = user && !user.isAnonymous ? user : null;
    const names = identified
        ? memberNameParts(identified.name)
        : { firstName: null, lastName: null };
    return {
        isLoaded: !session.isPending,
        user: identified
            ? {
                  createdAt: identified.createdAt,
                  email: identified.email,
                  firstName: names.firstName,
                  id: identified.id,
                  image: identified.image ?? null,
                  lastName: names.lastName,
                  name: identified.name,
              }
            : null,
    };
}

export function useAuthActions() {
    return {
        signOut: async ({ redirectUrl = "/" } = {}) => {
            const { error } = await authClient.signOut();
            if (error) {
                throw new Error(error.message ?? "Sign out could not complete");
            }
            window.location.assign(redirectUrl);
        },
    };
}

export async function refetchAuthSession() {
    await authClient.$store.atoms.session.get().refetch({
        query: { disableCookieCache: true },
    });
}

export async function signInWithGoogle(callbackURL: string) {
    const { error } = await authClient.signIn.social({
        provider: "google",
        callbackURL,
        errorCallbackURL: oauthErrorCallbackUrl(callbackURL),
    });
    if (error) {
        throw new Error(error.message ?? "Google sign-in could not start");
    }
}

export function useGoogleSignInStart({
    onFailure,
}: { onFailure?: () => void } = {}) {
    const [status, setStatus] = useState<GoogleSignInStartStatus>("idle");
    useUnsafeEffect(() => {
        const resetAfterBackNavigation = (event: PageTransitionEvent) => {
            if (event.persisted) setStatus("idle");
        };
        window.addEventListener("pageshow", resetAfterBackNavigation);
        return () =>
            window.removeEventListener("pageshow", resetAfterBackNavigation);
    }, []);
    return {
        failed: status === "failed",
        pending: status === "pending",
        start: (callbackURL: string) =>
            startGoogleSignInWith(signInWithGoogle, callbackURL, (next) => {
                setStatus(next);
                if (next === "failed") onFailure?.();
            }),
    };
}

export async function listLinkedProviders(): Promise<string[]> {
    const { data, error } = await authClient.listAccounts();
    if (error) throw new Error(error.message ?? "Could not load accounts");
    return (data ?? []).map((account) => account.providerId);
}

export async function linkGoogleAccount(callbackURL: string) {
    const { error } = await authClient.linkSocial({
        provider: "google",
        callbackURL,
        errorCallbackURL: oauthErrorCallbackUrl(callbackURL),
    });
    if (error) {
        throw new Error(error.message ?? "Google sign-in could not start");
    }
}
